import React from "react";
import { ArrowRight } from "lucide-react";
import { ProductCollectionFilter } from "./productCollection.shared";
import {
  formatProductPrice,
  productSliderStyles,
} from "./ProductSlider.styles";

export type ProductSliderLinkComponent = React.ComponentType<{
  href: string;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  [key: string]: any;
}>;

export type ProductSliderItem = {
  id: string;
  slug: string;
  name: string;
  internalLink: string;
  description?: string;
  parentId?: string;
  categoryId?: string;
  productId?: string;
  sortOrder?: number;
  flags?: {
    isFeatured?: boolean;
    isClearance?: boolean;
    isAusMade?: boolean;
    isInStock?: boolean;
  };
  basic?: {
    tags?: string[];
    active?: boolean;
  };
  featureImage?: {
    original?: string;
    thumbnail?: string;
    alt?: string;
  };
  pricing?: Array<{
    label?: string;
    price?: number | null;
  }>;
};

export type ProductSliderBaseProps = {
  items: ProductSliderItem[];
  Link: ProductSliderLinkComponent;
  title?: string;
  eyebrow?: string;
  description?: string;
  currency?: string;
  ctaText?: string;
  filter?: ProductCollectionFilter;
  emptyTitle?: string;
  emptyDescription?: string;
};

export function ProductSliderCard({
  item,
  Link,
  currency = "AUD",
  ctaText = "View Product",
}: {
  item: ProductSliderItem;
  Link: ProductSliderLinkComponent;
  currency?: string;
  ctaText?: string;
}) {
  const imageUrl = item.featureImage?.thumbnail || item.featureImage?.original || "";
  const imageAlt = item.featureImage?.alt || item.name;
  const firstPrice = item.pricing?.[0];
  const formattedPrice = formatProductPrice(firstPrice?.price, currency);

  return (
    <Link
      href={item.internalLink}
      style={productSliderStyles.tileLink}
      aria-label={`Open ${item.name}`}
    >
      <article className="product-slider-card" style={productSliderStyles.tile}>
        <div className="product-slider-card-image" style={productSliderStyles.imageWrap}>
          {imageUrl ? (
            <img src={imageUrl} alt={imageAlt} style={productSliderStyles.image} />
          ) : (
            <div style={productSliderStyles.imageFallback}>{item.name}</div>
          )}
        </div>

        <div style={productSliderStyles.content}>
          <div style={productSliderStyles.contentTop}>
            <h3 style={productSliderStyles.productName}>{item.name}</h3>

            {item.description ? (
              <p style={productSliderStyles.productDescription}>{item.description}</p>
            ) : null}
          </div>

          <div style={productSliderStyles.contentBottom}>
            <div style={productSliderStyles.footerRow}>
              <div style={productSliderStyles.pricingRow}>
                {firstPrice?.label ? (
                  <p style={productSliderStyles.priceLabel}>{firstPrice.label}</p>
                ) : null}
                <p style={productSliderStyles.priceValue}>{formattedPrice}</p>
              </div>

              <div style={productSliderStyles.ctaWrap}>
                <p style={productSliderStyles.ctaText}>{ctaText}</p>
                <span style={productSliderStyles.iconButton} aria-hidden="true">
                  <ArrowRight size={18} strokeWidth={2.25} />
                </span>
              </div>
            </div>
          </div>
        </div>
      </article>
    </Link>
  );
}
